import React, { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { PutPricingSheetBulkUpdate } from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheet";
import BatchOptionGroup from "pages/secure/facility/settings/accounts/BatchOptionGroup";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Popup from "components/popup/Popup";
import { Select } from "components/select/index";
import TimePick from "components/timePick/TimePick";
import { ButtonNew } from "components/buttonNew";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { ICourse } from "redux/reducers/models/facility";
import { IProduct, IVariant } from "redux/reducers/models/product";

import PricingSheetProductQuickAdd from "./PricingSheetProductQuickAdd";

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const DAY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Holiday",
] as const;

type SelectedVariant = {
  id: number;
  productTitle: string;
  variantTitle: string;
  subtitle: string;
};

type BulkEditPricingSheetModalProps = {
  open: boolean;
  pricingSheetIds: number[];
  courses: ICourse[];
  isMultiCourse: boolean;
  onClose: () => void;
  onSuccess: (updatedCount: number) => void;
};

function selectedVariantFromProduct(product: IProduct, variant: IVariant): SelectedVariant {
  const productTitle = product.preferred_title || product.title || variant.title || "";
  const variantTitle = variant.title && variant.title !== productTitle ? variant.title : "";
  const subtitle = product.subtitle?.trim() && product.subtitle.trim() !== productTitle ? product.subtitle.trim() : "";

  return {
    id: variant.id,
    productTitle,
    variantTitle,
    subtitle,
  };
}

export default function BulkEditPricingSheetModal(props: BulkEditPricingSheetModalProps) {
  const { open, pricingSheetIds, courses, isMultiCourse, onClose, onSuccess } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const defaultCourseId = courses[0]?.id ?? null;

  const [formKey, setFormKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [applyDateRange, setApplyDateRange] = useState(false);
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate, setEndDate] = useState(() => new Date());
  const [applyTimeRange, setApplyTimeRange] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [applyDay, setApplyDay] = useState(false);
  const [dayOfWeek, setDayOfWeek] = useState<string>("Monday");
  const [applyCourse, setApplyCourse] = useState(false);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [applyProducts, setApplyProducts] = useState(false);
  const [variants, setVariants] = useState<SelectedVariant[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const today = new Date();
    setApplyDateRange(false);
    setStartDate(today);
    setEndDate(today);
    setApplyTimeRange(false);
    setStartTime("");
    setEndTime("");
    setApplyDay(false);
    setDayOfWeek("Monday");
    setApplyCourse(false);
    setCourseId(defaultCourseId);
    setApplyProducts(false);
    setVariants([]);
    setSubmitting(false);
    setFormKey(key => key + 1);
  }, [open, defaultCourseId]);

  const dateRangeReady = applyDateRange && startDate != null && endDate != null;
  const timeRangeReady = applyTimeRange && TIME_PATTERN.test(startTime) && TIME_PATTERN.test(endTime);
  const dayReady = applyDay && dayOfWeek !== "";
  const courseReady = applyCourse && courseId != null;
  const productsReady = applyProducts && variants.length > 0;
  const checkedOptionIncomplete =
    (applyDateRange && !dateRangeReady) ||
    (applyTimeRange && !timeRangeReady) ||
    (applyDay && !dayReady) ||
    (applyCourse && !courseReady) ||
    (applyProducts && !productsReady);
  const canApply =
    pricingSheetIds.length > 0 &&
    !checkedOptionIncomplete &&
    (dateRangeReady || timeRangeReady || dayReady || courseReady || productsReady);

  function addVariant(product: IProduct, variant: IVariant) {
    setVariants(prev => {
      if (prev.some(selected => selected.id === variant.id)) {
        return prev;
      }

      return [...prev, selectedVariantFromProduct(product, variant)];
    });
  }

  function removeVariant(variantId: number) {
    setVariants(prev => prev.filter(selected => selected.id !== variantId));
  }

  async function submitBulkEdit() {
    if (!canApply || submitting) {
      return;
    }

    const payload: Parameters<typeof PutPricingSheetBulkUpdate>[0] = {
      pricing_sheet_ids: pricingSheetIds,
    };

    if (applyDateRange) {
      payload.start_date = moment(startDate).format("MM-DD");
      payload.end_date = moment(endDate).format("MM-DD");
    }
    if (applyTimeRange) {
      payload.start_time = startTime;
      payload.end_time = endTime;
    }
    if (applyDay) {
      payload.day_of_week = dayOfWeek;
    }
    if (applyCourse && courseId != null) {
      payload.course_id = courseId;
    }
    if (applyProducts) {
      payload.variant_ids = variants.map(variant => variant.id);
    }

    setSubmitting(true);
    const res = await PutPricingSheetBulkUpdate(payload, true);
    setSubmitting(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.message || t("Error updating pricing sheets")));
      return;
    }

    onSuccess(res.data?.updated_count ?? pricingSheetIds.length);
  }

  return (
    <Popup
      open={open}
      className="pricing-sheets-bulk-edit-popup"
      type="info"
      aligned="left"
      title={t("Bulk Edit")}
      description={t(
        "Update shared settings on {{count}} selected pricing sheet(s). Only checked fields are changed.",
        { count: pricingSheetIds.length },
      )}
      okText={t("Apply")}
      onCancel={onClose}
      onOk={() => void submitBulkEdit()}
      loading={submitting}
      okDisabled={submitting || !canApply}
    >
      <div className="pricing-sheets-bulk-edit-fields">
        <BatchOptionGroup
          aria-label={t("Bulk edit fields")}
          options={[
            {
              key: "date_range",
              title: t("Date range"),
              description: t("Set the same season dates on every selected pricing sheet."),
              selected: applyDateRange,
              onSelect: () => setApplyDateRange(prev => !prev),
              content: (
                <DatePickerInput
                  key={`bulk-dates-${formKey}`}
                  label={t("Date range")}
                  months={1}
                  position="left"
                  usePortal
                  startingDate={startDate}
                  setStartingDate={setStartDate}
                  endingDate={endDate}
                  setEndingDate={setEndDate}
                  monthOnlyCalendar
                  hideDaysOfWeek
                  hideQuickOptions
                />
              ),
            },
            {
              key: "time_range",
              title: t("Time range"),
              description: t("Set the same start and end time on every selected pricing sheet."),
              selected: applyTimeRange,
              onSelect: () => setApplyTimeRange(prev => !prev),
              content: (
                <div className="pricing-sheets-bulk-edit-times">
                  <TimePick
                    value={startTime}
                    onChange={timeString => setStartTime(timeString ?? "")}
                    label={t("Start time")}
                    size="large"
                  />
                  <TimePick
                    value={endTime}
                    onChange={timeString => setEndTime(timeString ?? "")}
                    label={t("End time")}
                    size="large"
                  />
                </div>
              ),
            },
            {
              key: "day",
              title: t("Day of week"),
              description: t("Move every selected pricing sheet to the same day."),
              selected: applyDay,
              onSelect: () => setApplyDay(prev => !prev),
              content: (
                <Select
                  key={`bulk-day-${formKey}`}
                  label={t("Day of week")}
                  defaultValue={dayOfWeek}
                  onChange={(value: string) => setDayOfWeek(value)}
                >
                  {DAY_OPTIONS.map(day => (
                    <Option key={day} value={day}>
                      {t(day)}
                    </Option>
                  ))}
                </Select>
              ),
            },
            ...(isMultiCourse
              ? [
                  {
                    key: "course",
                    title: t("Course"),
                    description: t("Assign every selected pricing sheet to the same course."),
                    selected: applyCourse,
                    onSelect: () => setApplyCourse(prev => !prev),
                    content: (
                      <Select
                        key={`bulk-course-${formKey}`}
                        label={t("Course")}
                        defaultValue={courseId ?? undefined}
                        onChange={(value: number) => setCourseId(value)}
                      >
                        {courses.map(course => (
                          <Option key={course.id} value={course.id}>
                            {course.long_name || course.full_name}
                          </Option>
                        ))}
                      </Select>
                    ),
                  },
                ]
              : []),
            {
              key: "products",
              title: t("Attached products"),
              description: t("Replace the products on every selected pricing sheet with this set."),
              selected: applyProducts,
              onSelect: () => setApplyProducts(prev => !prev),
              content: (
                <div className="pricing-sheets-bulk-edit-products-field">
                  <PricingSheetProductQuickAdd
                    addedVariantIds={variants.map(variant => variant.id)}
                    onAddVariant={addVariant}
                    helpText={null}
                    placeholder={t("Search to add products...")}
                  />
                  {variants.length > 0 && (
                    <ul className="pricing-sheets-bulk-edit-products">
                      {variants.map(variant => (
                        <li key={variant.id}>
                          <div className="pricing-sheets-bulk-edit-product-copy">
                            <span className="pricing-sheets-bulk-edit-product-title">{variant.productTitle}</span>
                            {variant.variantTitle ? (
                              <span className="pricing-sheets-bulk-edit-product-variant">{variant.variantTitle}</span>
                            ) : null}
                            {variant.subtitle ? (
                              <span className="pricing-sheets-bulk-edit-product-subtitle">{variant.subtitle}</span>
                            ) : null}
                          </div>
                          <ButtonNew
                            className="pricing-sheets-bulk-edit-product-remove"
                            type="text"
                            size="small"
                            onClick={() => removeVariant(variant.id)}
                          >
                            {t("Remove")}
                          </ButtonNew>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </Popup>
  );
}
